/**
 * The raw registration path: `exposedTo`, and unregistration by aborting the
 * signal supplied at registration.
 *
 * Two things make this path the one the hook cannot replace. Requirement 15.2 says
 * a chart scoped tool is unregistered by aborting the signal handed over at
 * registration, and requirements 21.4 and 26.4 name the origins a tool is exposed
 * to. Neither rides on a hook.
 *
 * `registerToolRaw` never throws. It awaits the registration promise, because an
 * invalid descriptor and a duplicate name both reject, and converts a rejection into
 * a typed {@link RegistrationOutcome} so the tool list panel can state the cause,
 * per requirement 1.8. A failed registration is a state of the product, not an
 * exception in it.
 *
 * ### The signal goes in the second argument, and that is a measurement
 *
 * This file used to hand the signal over as a field on the descriptor and call
 * `registerTool(descriptor)` with one argument. On Chrome Canary 154.0.8032.0 with
 * `--enable-features=WebMCP` that registers the tool and then does nothing: the tool
 * is still listed after the signal is aborted. The same signal passed as
 * `registerTool(descriptor, { signal })` withdraws it. Requirements 15.2 and 15.5 did
 * not hold on that build until this changed, so the second argument is now the route
 * and the descriptor field is kept only as belt and braces.
 *
 * Re-registering a name is not a way around it either. On that build a name already
 * in use rejects with `InvalidStateError: Duplicate tool name`, so the only way to
 * take a tool away is the signal.
 *
 * The descriptor's `execute` is wrapped rather than passed through. Everything
 * requirement 11.3 and 11.7 ask for happens in that wrapper: one output shape, one
 * character budget, and a thrown value converted into an `isError` result so
 * nothing throws across a tool boundary.
 */

import type { AbortSignalLike, ToolExecute, ToolSpec, ToolText } from '@jig/tools';

import { describeThrown, readMethod, readProperty, type ModelContextLike } from './host';
import {
  registrationRefused,
  registrationUnsupported,
  type RegistrationOutcome,
} from './outcome';
import {
  asToolText,
  boundToolText,
  errorToolText,
  retrievalHintFor,
  thrownToolText,
} from './output';
import type { RetrievalHint } from './output';

export interface RegisterRawOptions {
  /**
   * The signal that unregisters the tool when it is aborted. Req 15.2.
   *
   * A real `AbortSignal` rather than the structural `AbortSignalLike`, because this
   * one is handed to the host rather than read by a verb.
   */
  readonly signal: AbortSignal;
  /** The origins allowed to see the tool. Defaults to the spec's own field. Req 21.4, 26.4. */
  readonly exposedTo?: ReadonlyArray<string> | undefined;
  /**
   * Abort the previous registration of this name and resolve once it has settled.
   *
   * Supplied by the registry that holds the previous controller. Called only on a
   * duplicate name, and awaited before the one retry, because aborting and
   * re-registering in the same turn is a race. A chart panel deselected and
   * reselected in quick succession is exactly that race, which is the case
   * requirement 15.2 describes.
   */
  readonly retirePrevious?: (() => void | PromiseLike<void>) | undefined;
  /** Override the verb named in a truncation hint. Defaults from the spec. Req 11.3. */
  readonly retrieval?: RetrievalHint | undefined;
}

/**
 * Which signal a verb should read: the one the host passed to this call where it
 * passed one, and otherwise the one supplied at registration.
 *
 * A host that hands the signal over as the second argument itself, rather than in a
 * context object, is covered too. Both shapes are unsettled on a draft API, and a
 * verb needs one thing from a signal, so reading either is cheaper than being wrong.
 */
const signalFor = (ctx: unknown, atRegistration: AbortSignalLike): AbortSignalLike => {
  const inContext = readProperty(ctx, 'signal');
  if (typeof readProperty(inContext, 'aborted') === 'boolean') return inContext as AbortSignalLike;
  if (typeof readProperty(ctx, 'aborted') === 'boolean') return ctx as AbortSignalLike;
  return atRegistration;
};

/**
 * Run a verb and return something an agent can read, whatever the verb did.
 *
 * The input arrives from the host untyped and is passed through as the verb's input
 * type. Validating it is the broker's job, not the wrapper's: a verb owns the
 * refusal sentence for a bad argument, and requirement 9.1 wants that sentence to
 * name the rule rather than the schema.
 */
export const runVerb = async <I>(
  spec: ToolSpec<I>,
  execute: ToolExecute<I>,
  hint: RetrievalHint,
  input: unknown,
  signal: AbortSignalLike,
): Promise<ToolText> => {
  try {
    const returned = await execute(input as I, { signal });
    const text = asToolText(returned);
    if (text === null) {
      return boundToolText(
        errorToolText(
          `${spec.name} could not complete. It returned a result that is neither a plain string nor a content array of text parts.`,
        ),
        hint,
      );
    }
    return boundToolText(text, hint);
  } catch (thrown) {
    return boundToolText(thrownToolText(spec.name, thrown), hint);
  }
};

/**
 * The object handed to `registerTool`.
 *
 * A plain record, because the host's descriptor type is not ours to import. Only the
 * fields a host reads go on it: `writes` and `carriesExternalContent` are our own
 * declarations, which `defineTool` has already turned into the two annotations, so
 * sending them would be sending the same fact twice. `exposedTo` is set or left off
 * rather than set to undefined, since a build that reads it at all could read an
 * explicit undefined as an empty exposure list.
 *
 * `signal` is on the descriptor **and** in the options object {@link registerOptions}
 * builds, which is the one place this file sends the same fact twice on purpose. The
 * options object is the route that was measured to work: on Chrome Canary
 * 154.0.8032.0 a signal supplied only here, on the descriptor, was accepted and then
 * did nothing, and the tool stayed listed after the abort. The field stays because
 * the design already records both shapes as unsettled on a draft API, a build that
 * reads the field is not hypothetical, and one extra property on an object costs
 * nothing. It is belt and braces, not the route.
 */
export const buildDescriptor = <I>(
  spec: ToolSpec<I>,
  execute: ToolExecute<I>,
  opts: RegisterRawOptions,
): Record<string, unknown> => {
  const hint = opts.retrieval ?? retrievalHintFor(spec);
  const exposedTo = opts.exposedTo ?? spec.exposedTo;

  const descriptor: Record<string, unknown> = {
    name: spec.name,
    description: spec.description,
    inputSchema: spec.inputSchema,
    annotations: spec.annotations,
    signal: opts.signal,
    execute: (input: unknown, ctx?: unknown): Promise<ToolText> =>
      runVerb(spec, execute, hint, input, signalFor(ctx, opts.signal)),
  };
  if (exposedTo !== undefined) descriptor.exposedTo = exposedTo;

  return descriptor;
};

/**
 * The second argument to `registerTool`, which is where the signal actually counts.
 *
 * Separated from {@link buildDescriptor} so both attempts of a duplicate name retry
 * send the same object, and so the one thing in it has a name a reader can search
 * for. Driving Chrome Canary 154.0.8032.0 headless with `--enable-features=WebMCP`
 * against the deployed site, `registerTool(descriptor, { signal })` left the tool off
 * the list after an abort and `registerTool(descriptor)` with the signal on the
 * descriptor left it on. That is one version on one machine and a draft API will
 * move, so this is recorded as an observation rather than as a specification.
 */
const registerOptions = (opts: RegisterRawOptions): Record<string, unknown> => ({
  signal: opts.signal,
});

interface Retirement {
  readonly retired: boolean;
  readonly note: string;
}

/**
 * Clear the name before retrying it.
 *
 * The caller's route comes first, since the registry holding the previous
 * `AbortController` is the only thing that can abort it, and requirement 15.2 makes
 * that abort the unregistration. With no controller behind the duplicate, which is
 * what a double mount leaves, there is nothing to await and so no retry either:
 * registering again into an occupied name would fail the same way and report a
 * second time.
 *
 * The `unregisterTool` fallback below is kept guarded, and it is honestly dead on
 * every browser we have measured. Chrome Canary 154.0.8032.0 exposes four members on
 * `document.modelContext`, being `ontoolchange`, `executeTool`, `getTools` and
 * `registerTool`, and no removal method at all. So on that build a duplicate name
 * with no controller behind it cannot be cleared, and this function says so in the
 * note rather than pretending a retry happened.
 */
const retirePreviousRegistration = async (
  context: ModelContextLike | null | undefined,
  name: string,
  supplied: (() => void | PromiseLike<void>) | undefined,
): Promise<Retirement> => {
  if (supplied !== undefined) {
    try {
      await supplied();
      return { retired: true, note: '' };
    } catch (thrown) {
      const { name: errorName } = describeThrown(thrown);
      return {
        retired: false,
        note: `Aborting the previous registration of ${name} failed with ${errorName}, so the name was not retried.`,
      };
    }
  }

  const unregister = readMethod(context, 'unregisterTool');
  if (unregister === null) {
    return {
      retired: false,
      note: `There was no previous registration to abort and this browser has no unregisterTool, so ${name} was not retried.`,
    };
  }

  try {
    await unregister.call(context, name);
    return { retired: true, note: '' };
  } catch (thrown) {
    const { name: errorName } = describeThrown(thrown);
    return {
      retired: false,
      note: `Unregistering the previous ${name} failed with ${errorName}, so the name was not retried.`,
    };
  }
};

const attemptRegistration = async (
  context: ModelContextLike | null | undefined,
  descriptor: Record<string, unknown>,
  options: Record<string, unknown>,
  name: string,
): Promise<RegistrationOutcome> => {
  const register = readMethod(context, 'registerTool');
  if (register === null) {
    return registrationUnsupported(
      name,
      `This browser has no document.modelContext.registerTool, so ${name} was not registered. Every part of the workbench stays reachable through the interface.`,
    );
  }

  try {
    // Two arguments, because the second is where the signal is read from on the one
    // build we have measured. A host that wants only the descriptor ignores the
    // extra argument, which is what a JavaScript function does with one.
    await register.call(context, descriptor, options);
    return { ok: true, name };
  } catch (thrown) {
    return registrationRefused(name, describeThrown(thrown));
  }
};

/**
 * Register one tool through the raw path, and report what happened.
 *
 * The model context is a parameter. This package never reads `document`, because
 * `apps/jig/src/platform` is the single feature detection point and requirement 20.3
 * with Property S2 only hold while it stays the only one.
 *
 * On a duplicate name the previous registration is retired, that retirement is
 * awaited, and the registration is attempted once more. Once, not until it works: a
 * name that is still occupied after its holder has gone is a collision the build
 * validator should have caught, and a retry loop would hide it. Requirement 11.7.
 */
export async function registerToolRaw<I>(
  context: ModelContextLike | null | undefined,
  spec: ToolSpec<I>,
  execute: ToolExecute<I>,
  opts: RegisterRawOptions,
): Promise<RegistrationOutcome> {
  const descriptor = buildDescriptor(spec, execute, opts);
  const options = registerOptions(opts);
  const first = await attemptRegistration(context, descriptor, options, spec.name);
  if (first.ok || first.cause !== 'DuplicateName') return first;

  const retirement = await retirePreviousRegistration(context, spec.name, opts.retirePrevious);
  if (!retirement.retired) {
    return { ...first, message: `${first.message} ${retirement.note}` };
  }

  const second = await attemptRegistration(context, descriptor, options, spec.name);
  if (second.ok) return second;
  return {
    ...second,
    message: `${second.message} The previous registration of ${spec.name} was cleared first and the name was still refused.`,
  };
}
